import React, { useMemo } from "react";
import { Box, Card, CardBody, Heading } from "@chakra-ui/react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    AXIS_DEFAULTS,
    BAR_FILL,
    BAR_STROKE,
    CHART_MARGIN,
    INTEGER_TICK_FORMATTER,
} from "@config/chartConfig";
import type { MappedDashboardStats } from "@modules/ytg/hooks/useYtgDashboardStats";

interface YtgLanguageChartProps {
    stats: MappedDashboardStats | null | undefined;
}

/**
 * Global dashboard bar chart: breakdown of operations by language. Renders
 * nothing until the statistics are available.
 */
export const YtgLanguageChart = ({ stats }: YtgLanguageChartProps): JSX.Element | null => {
    const data = useMemo(() => {
        if (!stats?.by_lang_list) return null;
        return stats.by_lang_list.map((l) => ({
            name: `${l.emoji_flag} ${l.description}`,
            count: l.count,
        }));
    }, [stats]);

    if (!data) return null;

    return (
        <Card>
            <CardBody>
                <Heading as="h2" size="md" mb={4} color="gray.700">
                    Répartition par langue
                </Heading>
                <Box h="300px">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={CHART_MARGIN}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" {...AXIS_DEFAULTS} />
                            <YAxis allowDecimals={false} tickFormatter={INTEGER_TICK_FORMATTER} {...AXIS_DEFAULTS} />
                            <Tooltip />
                            <Bar
                                dataKey="count"
                                name="Opérations par langue"
                                fill={BAR_FILL}
                                stroke={BAR_STROKE}
                                strokeWidth={2}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </CardBody>
        </Card>
    );
};
